import { mediaQuery } from '@noths/polaris-client-styles';
import { themeVariables } from '@noths/polaris-design-system-abstract-theme';
import { css } from 'styled-components';

const { borderRadius, colors, spacing, typography } = themeVariables;

// Width (px) the hero widens at. The design system ships no breakpoint tokens, so the hero owns
// the width it responds to, the same way HeroCarouselItem.styles.ts does.
export const BREAKPOINT_M = 768;

export const container = css`
  margin: 0 auto;
  max-width: 1408px; /* largest supported hero width */
  width: 100%;
`;

export const media = css`
  aspect-ratio: 4 / 5; /* portrait crop of the 16:9 asset on small screens (Figma frame: 1080x1350) */
  cursor: pointer;
  overflow: hidden;
  position: relative;
  width: 100%;

  ${mediaQuery(
    { min: BREAKPOINT_M },
    css`
      /* The design's cinematic banner: 1408x528, like the hero carousel, not the video's 16:9. */
      aspect-ratio: 1408 / 528;
      /* Rounded on large screens only; the full-bleed mobile hero has square corners. */
      border-radius: var(${borderRadius.md.desktop});
    `,
  )}
`;

export const still = css`
  height: 100%;
  left: 0;
  object-fit: cover;
  position: absolute;
  top: 0;
  width: 100%;
`;

export const player = css`
  --controls: none;
  /* The box is a crop (4:5 mobile, 1408:528 desktop) of the 16:9 asset; fill it, never letterbox. */
  --media-object-fit: cover;

  height: 100%;
  left: 0;
  pointer-events: none;
  position: absolute;
  top: 0;
  transition: opacity 400ms ease;
  width: 100%;
`;

export const heading = css`
  color: var(${colors.content.action});
  font-family: var(${typography.heading.fontFamily});
  /* heading-LG at every breakpoint: the Figma mobile heading uses the same token as desktop. */
  font-size: var(${typography.heading.sizes.lg.fontSize});
  font-weight: var(${typography.heading.fontWeight});
  left: 0;
  letter-spacing: var(${typography.heading.letterSpacing});
  line-height: var(${typography.heading.sizes.lg.lineHeight});
  margin: 0;
  /* The design's heading band: 20px vertical, 24px horizontal. */
  padding: 20px var(${spacing.xl});
  /* Copy sits above the media but must never block it; the play button is the only control. */
  pointer-events: none;
  position: absolute;
  right: 0;
  z-index: 2;

  /* The colour gradient. The blur is a separate layer (see blurLayer) so it can fade independently. */
  &::before {
    content: '';
    height: 100%;
    left: 0;
    pointer-events: none;
    position: absolute;
    right: 0;
    z-index: -1;
  }
`;

export const headingTop = css`
  top: 0;

  &::before {
    background: linear-gradient(to bottom, rgba(0, 0, 0, 0.6) 0%, rgba(30, 30, 41, 0) 100%);
    top: 0;
  }
`;

export const headingBottom = css`
  bottom: 0;

  &::before {
    background: linear-gradient(to top, rgba(0, 0, 0, 0.6) 0%, rgba(30, 30, 41, 0) 100%);
    bottom: 0;
  }
`;

/*
 * The blur, as its own layer behind the gradient (z-index: -2). The mask lives on this wrapper and
 * the backdrop-filter on its child (blurInner) on purpose: a backdrop-filter on a *masked* element
 * doesn't feather, the blur cuts off at a hard edge. Same construction as HeroCarouselItem.
 */
export const blurLayer = css`
  height: 100%;
  left: 0;
  pointer-events: none;
  position: absolute;
  right: 0;
  z-index: -2;
`;

export const blurLayerTop = css`
  -webkit-mask-image: linear-gradient(to bottom, #000 0%, transparent 100%);
  mask-image: linear-gradient(to bottom, #000 0%, transparent 100%);
  top: 0;
`;

export const blurLayerBottom = css`
  -webkit-mask-image: linear-gradient(to top, #000 0%, transparent 100%);
  mask-image: linear-gradient(to top, #000 0%, transparent 100%);
  bottom: 0;
`;

export const blurInner = css`
  -webkit-backdrop-filter: blur(5px);
  backdrop-filter: blur(5px);
  inset: 0;
  position: absolute;
`;

export const playButton = css`
  align-items: center;
  background: var(${colors.surface.default});
  border: none;
  border-radius: 50%;
  color: var(${colors.content.default});
  cursor: pointer;
  display: flex;
  height: 48px; /* thumb-sized tap target */
  justify-content: center;
  left: 50%;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 48px;
`;

export const copyRow = css`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: var(${spacing.sm});
  padding: var(${spacing.md});
  text-align: center;

  ${mediaQuery(
    { min: BREAKPOINT_M },
    css`
      display: grid;
      gap: var(${spacing.md});
      grid-template-columns: 1fr auto;
      text-align: left;
    `,
  )}
`;

export const paragraph = css`
  font-size: var(${typography.body.sizes.md.fontSize});
  line-height: var(${typography.body.sizes.md.lineHeight});
  margin: 0;
`;

export const ctaButtons = css`
  display: flex;
  flex-direction: column;
  gap: var(${spacing.sm});
  width: 100%;

  ${mediaQuery(
    { min: BREAKPOINT_M },
    css`
      flex-direction: row;
      grid-row: 1 / span 2;
      grid-column: 2;
      width: auto;
    `,
  )}
`;

export const terms = css`
  color: var(${colors.content.tertiary});
  font-size: var(${typography.body.sizes.sm.fontSize});
  line-height: var(${typography.body.sizes.sm.lineHeight});
  margin: 0;
`;
